


















































import { cloneDeep } from "lodash-es";
import { mixins } from "vue-class-component";
import { Component, Prop, Provide, Watch } from "vue-property-decorator";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import WithContentFeed from "@/mixins/WithContentFeed";
import providers from "@/lib/api";
import { AuthorisedOnly } from "@/lib/decorators";
import config from "@/config";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import ContentFeedItem from "@/components/ContentFeedItem";
import InfiniteScroll from "@/components/InfiniteScroll";
import PageTitle from "@/components/PageTitle";
import PostTipping from "@/components/PostTipping";

@Component({
  name: "ContentFeedList",
  components: { BaseConfirm, BaseButton, PageTitle, InfiniteScroll, PostTipping },
})
export default class ContentFeedList extends mixins(WithContentFeed) {
  @Prop({ required: true })
  public readonly scroll!: boolean;

  @Prop({ required: true })
  public contentFeedList!: ContentFeed[];

  @Prop({ type: String, default: undefined })
  public readonly scrollToFeed!: string | undefined;

  @Prop({ type: Array, required: true })
  public readonly subscriptionLevels!: SubscriptionLevel[];

  @Prop({ type: Array, required: true })
  public readonly paymentPackages!: PaymentPackage[];

  @Prop({ type: Boolean, default: false })
  public readonly showFollowButton!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showReportItem!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showComment!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showFooter!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showShare!: boolean;

  @Prop({ default: () => new Map() })
  public readonly followedUsersId!: Map<string, boolean>;

  public scrollToFeedIndex = 0;

  public feedId: string | null = null;

  public observer: IntersectionObserver | null = null;

  public showDeleteFeedConfirm: boolean = false;

  public processingDelete: boolean = false;

  public feedIdToDelete: string | null = null;

  public getItemView = ContentFeedItem;

  public get showTip() {
    return !!this.providedData.user && config.featureTippingPosts;
  }

  public closeTip() {
    this.providedData.user = null;
  }

  @Provide("messageData")
  public providedData: { user: UserPublicData | null } = {
    user: null,
  };

  @AuthorisedOnly
  public async handleLike(feedId: string) {
    let likedFeed = cloneDeep(this.contentFeedList.find(i => i.id === feedId));
    if (!likedFeed) return;
    const likedResponse = await providers.LikeProvider.toggle({
      subject: { subjectId: feedId, subjectType: "content_set" },
    });
    likedFeed.likes.liked = likedResponse;
    if (likedResponse) {
      likedFeed.likes.count++;
    } else {
      likedFeed.likes.count = Math.max(0, likedFeed.likes.count - 1);
    }
    this.$emit("update:contentFeed", likedFeed);
  }

  @AuthorisedOnly
  public handleTip(item: ContentFeed) {
    this.feedId = item.id;
    this.providedData.user = item.user;
  }

  @AuthorisedOnly
  public async handleFollow(userId: string, cb: Function) {
    try {
      const followStatus = await providers.FollowingsProvider.toggle({ id: userId });
      this.$emit("follow", userId, followStatus);
    } finally {
      cb();
    }
  }

  public showDeleteFeed(feedId: string) {
    this.feedIdToDelete = feedId;
    this.showDeleteFeedConfirm = true;
  }

  public async deleteFeed() {
    if (!this.feedIdToDelete) return;
    try {
      this.processingDelete = true;
      if (await this.contentFeedDeletePost(this.feedIdToDelete)) {
        this.$emit("delete", this.feedIdToDelete);
        this.showDeleteFeedConfirm = false;
      }
    } finally {
      this.processingDelete = false;
    }
  }

  @Watch("showDeleteFeedConfirm")
  public onChangeShowConfirm(newState: boolean) {
    if (!newState) this.feedIdToDelete = null;
  }

  @Watch("scrollToFeed", { immediate: true })
  public async scrollToFeedWatcher(feedIdNew?: string | null, feedIdOld?: string | null) {
    if (!feedIdNew || !!feedIdOld) return;
    const selectedIndex = this.contentFeedList.findIndex(i => i.id === feedIdNew);
    this.scrollToFeedIndex = selectedIndex;
    if (selectedIndex > 0) {
      await this.$nextTick();
      const el = this.$el.querySelector(`[data-id="${feedIdNew}"]`);
      el && el.scrollIntoView({ block: "start", behavior: "auto" });
    } else {
      window.scrollTo({ top: 0 });
    }
  }

  public orientationChangeHandler() {
    this.scrollToFeedWatcher(this.feedId);
  }

  public async setObserve(el: HTMLElement) {
    if (!this.observer) {
      this.observer = new IntersectionObserver(entries => this.observerCb(entries), {
        rootMargin: "-40% 0px -60% 0px",
      });
    }
    this.observer.observe(el);
  }

  public observerCb(entries: IntersectionObserverEntry[]) {
    let entry = entries.find(e => e.isIntersecting);
    if (!entry) return;
    const feedId = (entry.target as HTMLElement).dataset.id || null;
    this.feedId = feedId;
    this.$emit("feedOnScreen", feedId);
  }

  public mounted() {
    window.addEventListener("orientationchange", this.orientationChangeHandler);
  }

  public beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener("orientationchange", this.orientationChangeHandler);
  }
}
